import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_RESET_DELAY_MS = 2000;

export default function useClipboardCopy(options = {}) {
  const { resetDelay = DEFAULT_RESET_DELAY_MS, onSuccess, onError } = options;
  const [copied, setCopied] = useState(false);
  const [isClipboardSupported, setIsClipboardSupported] = useState(false);
  const resetTimeoutRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    setIsClipboardSupported(
      typeof navigator !== "undefined" &&
        typeof navigator.clipboard?.writeText === "function",
    );
  }, []);

  const clearResetTimeout = useCallback(() => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearResetTimeout, [clearResetTimeout]);

  const copyToClipboard = useCallback(
    async (value) => {
      if (!isClipboardSupported) {
        onErrorRef.current?.(new Error("Clipboard API unavailable"));
        return false;
      }

      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        clearResetTimeout();
        resetTimeoutRef.current = setTimeout(() => {
          setCopied(false);
          resetTimeoutRef.current = null;
        }, resetDelay);
        onSuccessRef.current?.(value);
        return true;
      } catch (error) {
        onErrorRef.current?.(error);
        return false;
      }
    },
    [clearResetTimeout, isClipboardSupported, resetDelay],
  );

  return {
    copied,
    copyToClipboard,
    isClipboardSupported,
  };
}
